import { Loader2 } from "lucide-react";

import { VacancyDataSection } from "./VacancyDataSection";
import { VacancyField } from "./VacancyField";
import { VacancyTextFields } from "./VacancyTextFields";

import { Badge } from "@/components/ui/v2/badge";
import { Separator } from "@/components/ui/v2/separator";
import { useVacancyDescriptions } from "@/features/vacancies/api";
import { getCombinedFeedContent, VacancyFieldConfig } from "@/lib/vacancies";
import { VacancyType } from "@/types/vacancy/VacancyType";

const getFieldContent = (
  vacancy: VacancyType,
  sectionName: string
): { header: string; content: string } => {
  switch (sectionName) {
  case "Summary":
    return {
      header: vacancy.summaryDisplayTitle || "",
      content: vacancy.summary || "",
    };
  case "FunctionDescription":
    return {
      header: vacancy.functionDescriptionDisplayTitle || "",
      content: vacancy.functionDescription || "",
    };
  case "ClientDescription":
    return {
      header: vacancy.clientDescriptionDisplayTitle || "",
      content: vacancy.clientDescription || "",
    };
  case "RequirementsDescription":
    return {
      header: vacancy.requirementsDescriptionDisplayTitle || "",
      content: vacancy.requirementsDescription || "",
    };
  case "OfferDescription":
    return {
      header: vacancy.offerDescriptionDisplayTitle || "",
      content: vacancy.offerDescription || "",
    };
  case "AdditionalDescription":
    return {
      header: vacancy.additionalDescriptionDisplayTitle || "",
      content: vacancy.additionalDescription || "",
    };
  default:
    return { header: "", content: "" };
  }
};

export const VacancyInfoWithConfiguration: React.FC<{
  vacancy: VacancyType;
  isFeedClient: boolean;
  clientId?: string;
}> = ({ vacancy, isFeedClient, clientId }) => {
  const { descriptions, descriptionsLoading: isLoading } =
    useVacancyDescriptions({
      groupNames: vacancy.publicationChannels,
      clientId,
    });

  if (isLoading) {
    return (
      <div className="pt-8">
        <VacancyDataSection vacancy={vacancy} />
        <Separator className="my-4" />
        <div className="flex w-full items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
  
    );
  }

  // If it's a feed client, show the combined feed content
  if (isFeedClient) {
    return (
      <div className="pt-8">
        <VacancyDataSection vacancy={vacancy} />
        <div className="pt-8 flex w-full flex-col">
          <VacancyField
            label="Description"
            text={getCombinedFeedContent(vacancy)}
            fullHeight
          />
        </div>
      </div>
    );
  }

  // For non-feed clients, show the configured descriptions
  const sortedDescriptions =
    descriptions?.sort((a, b) => a.displayOrder - b.displayOrder) || [];

  const fields: VacancyFieldConfig[] = sortedDescriptions.map((description) => {
    const { header, content } = getFieldContent(
      vacancy,
      description.descriptionSectionName
    );

    const displayLabel =
      description.label || description.descriptionSectionName;

    let headerText: string;
    let headerBadgeLabel: string;

    if (header) {
      headerText = header;
      headerBadgeLabel = "Vacancy Header";
    } else if (description.customHeader) {
      headerText = description.customHeader;
      headerBadgeLabel = "Client Header";
    } else {
      headerText = "";
      headerBadgeLabel = "No Header";
    }

    return {
      key: description.descriptionSectionName,
      headerLabel: `${displayLabel} Header`,
      contentLabel: displayLabel,
      header: headerText,
      content: content,
      headerBadge: (
        <Badge variant="neutral" size="large">
          {headerBadgeLabel}
        </Badge>
      ),
    };
  });

  return (
    <div className="pt-8">
      <VacancyDataSection vacancy={vacancy} />
      <Separator className="my-4" />
      <VacancyTextFields fields={fields} />
    </div>
  );
};
